import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dossiersCommerciaux } from '../services/api';
import { PdfThumbnail, DocumentViewer } from './shared/DocumentViewer';
// ⚠ `Litiges.css` fournit `drop-zone` / `drop-zone-content`. Elle est chargée
// par ailleurs quand le module Litiges est monté — mais s'appuyer là-dessus est
// un hasard, pas un contrat : on l'importe explicitement.
import './Litiges.css';
import '../styles/GestionCommerciale.css';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  en_cours: 'En cours',
  documents_complets: 'Docs complets',
  livre: 'Livré',
  annule: 'Annulé',
  archive: 'Archivé',
};

const STATUS_CLASSES = {
  en_cours: 'status-info',
  documents_complets: 'status-warning',
  livre: 'status-success',
  annule: 'status-error',
  archive: '',
};

const DOC_STATUS_LABELS = {
  manquant: 'Manquant',
  uploade: 'Uploadé',
  valide: 'Validé',
  refuse: 'Refusé',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatMoney(v) {
  if (v === null || v === undefined || v === '') return '—';
  return parseFloat(v).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' o';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' Ko';
  return (bytes / 1048576).toFixed(1) + ' Mo';
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
function GestionCommerciale({ user, editMode, setHeaderAction }) {
  // ── State ──
  const [activeTab, setActiveTab] = useState('dossiers'); // 'dossiers' | 'livraisons' | 'detail'
  const [detailTab, setDetailTab] = useState('client');
  const [selectedDossierId, setSelectedDossierId] = useState(null);

  // List
  const [dossiers, setDossiers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({});
  const [vendeurs, setVendeurs] = useState([]);
  const [filters, setFilters] = useState({ search: '', statut: '', vendeur_id: '', type_vn_vo: '' });
  const [loading, setLoading] = useState(false);

  // Detail
  const [dossier, setDossier] = useState(null);
  const [slots, setSlots] = useState([]);
  const [extras, setExtras] = useState([]);
  const [messages, setMessages] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [saving, setSaving] = useState(false);

  // Modals
  const [showNewModal, setShowNewModal] = useState(false);
  const [showRefuseModal, setShowRefuseModal] = useState(null);
  const [refuseComment, setRefuseComment] = useState('');

  // Config
  const [configTypes, setConfigTypes] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({ nom: '', categorie: 'vendeur', obligatoire: false, ordre: 0, ia_prompt: '', rubrique: 'Dossier général' });
  const [editingType, setEditingType] = useState(null);

  // Selection for delete
  const [selectedIds, setSelectedIds] = useState([]);

  // Découpe → sidebar bridge (pages sélectionnées exposées au parent)
  const [decoupeSelectedPages, setDecoupeSelectedPages] = useState([]);
  const [decoupeSourceKey, setDecoupeSourceKey] = useState(null);
  const [decoupeDroppedPages, setDecoupeDroppedPages] = useState([]); // pages dropped via sidebar

  const LIMIT = 50;

  // ── Data loading ──
  const loadDossiers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filters.search) params.search = filters.search;
      if (filters.statut) params.statut = filters.statut;
      if (filters.vendeur_id) params.vendeur_id = filters.vendeur_id;
      if (filters.type_vn_vo) params.type_vn_vo = filters.type_vn_vo;
      const res = await dossiersCommerciaux.getAll(params);
      setDossiers(res.data.dossiers);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Erreur chargement dossiers:', e);
    }
    setLoading(false);
  }, [page, filters]);

  const loadStats = useCallback(async () => {
    try {
      const res = await dossiersCommerciaux.getStats();
      setStats(res.data.stats);
    } catch (e) { /* ignore */ }
  }, []);

  const loadVendeurs = useCallback(async () => {
    try {
      const res = await dossiersCommerciaux.getVendeurs();
      setVendeurs(res.data.vendeurs);
    } catch (e) { /* ignore */ }
  }, []);

  const loadDossier = useCallback(async (id) => {
    try {
      const res = await dossiersCommerciaux.getById(id);
      setDossier(res.data.dossier);
    } catch (e) {
      console.error('Erreur chargement dossier:', e);
    }
  }, []);

  const loadSlots = useCallback(async (id) => {
    try {
      const res = await dossiersCommerciaux.getDocumentSlots(id);
      setSlots(res.data.slots);
    } catch (e) { console.error('Erreur chargement slots:', e); }
  }, []);

  const loadExtras = useCallback(async (id) => {
    try {
      const res = await dossiersCommerciaux.getExtras(id);
      setExtras(res.data.extras);
    } catch (e) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (id) => {
    try {
      const res = await dossiersCommerciaux.getMessages(id);
      setMessages(res.data.messages);
    } catch (e) { /* ignore */ }
  }, []);

  const loadHistorique = useCallback(async (id) => {
    try {
      const res = await dossiersCommerciaux.getHistorique(id);
      setHistorique(res.data.historique);
    } catch (e) { /* ignore */ }
  }, []);

  const loadConfigTypes = useCallback(async () => {
    try {
      const res = await dossiersCommerciaux.getTypesDocuments();
      setConfigTypes(res.data.types);
    } catch (e) { /* ignore */ }
  }, []);

  // ── Effects ──
  useEffect(() => {
    if (activeTab === 'dossiers') {
      loadDossiers();
      loadStats();
      loadVendeurs();
    }
  }, [activeTab, loadDossiers, loadStats, loadVendeurs]);

  useEffect(() => {
    if (activeTab === 'detail' && selectedDossierId) {
      loadDossier(selectedDossierId);
      loadSlots(selectedDossierId);
      loadExtras(selectedDossierId);
      loadMessages(selectedDossierId);
      loadHistorique(selectedDossierId);
    }
  }, [activeTab, selectedDossierId, loadDossier, loadSlots, loadExtras, loadMessages, loadHistorique]);

  useEffect(() => {
    if (detailTab === 'configuration') {
      loadConfigTypes();
    }
  }, [detailTab, loadConfigTypes]);

  // Message polling
  useEffect(() => {
    if (activeTab !== 'detail' || detailTab !== 'messagerie' || !selectedDossierId) return;
    const interval = setInterval(() => loadMessages(selectedDossierId), 30000);
    return () => clearInterval(interval);
  }, [activeTab, detailTab, selectedDossierId, loadMessages]);

  // Header actions
  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'dossiers') {
      setHeaderAction({
        primary: { label: '+ Nouveau dossier', onClick: () => setShowNewModal(true) },
      });
    } else if (activeTab === 'livraisons') {
      setHeaderAction(null);
    } else {
      setHeaderAction(null);
    }
    return () => setHeaderAction && setHeaderAction(null);
  }, [activeTab, setHeaderAction]);

  // ── Actions ──
  const openDossier = (id) => {
    setSelectedDossierId(id);
    setDetailTab('client');
    setActiveTab('detail');
  };

  const backToList = () => {
    setActiveTab('dossiers');
    setSelectedDossierId(null);
    setDossier(null);
  };

  const handleCreateDossier = async (data) => {
    try {
      const res = await dossiersCommerciaux.create(data);
      setShowNewModal(false);
      openDossier(res.data.dossier.id);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleSaveDossier = async (fieldUpdates) => {
    if (!selectedDossierId) return;
    // ⚠ Délai minimum de 48 h sur la date de livraison (règle du process).
    // AVERTISSEMENT, pas blocage : une livraison exceptionnelle le jour même
    // existe, et un refus sec obligerait à contourner l'outil — ce qui coûte
    // plus cher que la règle ne rapporte. On s'assure seulement que le vendeur
    // sait ce qu'il fait.
    if (fieldUpdates.date_livraison_prevue) {
      const jours = (new Date(fieldUpdates.date_livraison_prevue).getTime() - Date.now()) / 86400000;
      if (jours < 2 && !window.confirm(
        'La livraison est prévue dans moins de 48 h.\n\n'
        + 'Le process demande un délai minimum de 48 h entre la saisie et la livraison.\n'
        + 'Confirmer cette date ?'
      )) { return; }
    }
    setSaving(true);
    try {
      const res = await dossiersCommerciaux.update(selectedDossierId, fieldUpdates);
      setDossier(res.data.dossier);
      // ⚠ On recharge les pièces après CHAQUE enregistrement, sans chercher à
      // deviner si le champ modifié compte.
      //
      // L'ancien test ne déclenchait le rechargement que pour `financement_type`
      // et `type_vn_vo` — une liste écrite en dur à l'époque où seuls ces deux
      // champs pouvaient conditionner une pièce. Depuis, une condition peut
      // porter sur N'IMPORTE QUELLE colonne du dossier, et les règles se
      // configurent à l'exécution : l'écran ne PEUT PAS savoir lesquelles
      // comptent. Résultat, saisir l'immatriculation d'une reprise ne faisait
      // apparaître ni la carte grise ni le certificat de cession jusqu'au
      // rechargement de la page (signalé le 2026-09-20).
      loadSlots(selectedDossierId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const handleChangeStatut = async (statut) => {
    if (!selectedDossierId) return;
    try {
      const res = await dossiersCommerciaux.updateStatut(selectedDossierId, { statut });
      setDossier(res.data.dossier);
      loadHistorique(selectedDossierId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteDossiers = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Supprimer ${selectedIds.length} dossier(s) ?`)) return;
    try {
      for (const id of selectedIds) {
        await dossiersCommerciaux.delete(id);
      }
      setSelectedIds([]);
      loadDossiers();
      loadStats();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  // ── Document actions ──
  const handleDocUpload = async (file, typeDocumentId) => {
    try {
      const presignRes = await dossiersCommerciaux.presignDocument(selectedDossierId, {
        filename: file.name,
        contentType: file.type,
        type_document_id: typeDocumentId,
      });
      const { uploadUrl, key } = presignRes.data;
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      await dossiersCommerciaux.confirmDocument(selectedDossierId, {
        key, filename: file.name, contentType: file.type, size: file.size, type_document_id: typeDocumentId,
      });
      loadSlots(selectedDossierId);
      loadHistorique(selectedDossierId);
    } catch (e) {
      alert('Erreur upload: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDocValidate = async (docId) => {
    try {
      await dossiersCommerciaux.validateDocument(selectedDossierId, docId);
      loadSlots(selectedDossierId);
      loadHistorique(selectedDossierId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDocRefuse = async () => {
    if (!showRefuseModal) return;
    try {
      await dossiersCommerciaux.refuseDocument(selectedDossierId, showRefuseModal, { commentaire: refuseComment });
      setShowRefuseModal(null);
      setRefuseComment('');
      loadSlots(selectedDossierId);
      loadHistorique(selectedDossierId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await dossiersCommerciaux.deleteDocument(selectedDossierId, docId);
      loadSlots(selectedDossierId);
      loadHistorique(selectedDossierId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  // ── Render ──
  if (activeTab === 'detail' && dossier) {
    return (
      <div className="page-container">
        <button className="dc-back-link" onClick={backToList}>
          ← Retour à la liste
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              {dossier.numero_dossier}
            </span>
            <span style={{ marginLeft: 12 }}>
              <span className={STATUS_CLASSES[dossier.statut] || ''}
                style={{ padding: '2px 10px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 600 }}>
                {STATUS_LABELS[dossier.statut]}
              </span>
            </span>
            <span style={{ marginLeft: 12, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {dossier.type_vn_vo} — {dossier.vehicule_marque} {dossier.vehicule_modele}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={dossier.statut}
              onChange={(e) => handleChangeStatut(e.target.value)}
              className="page-select"
              style={{ fontSize: '0.85rem' }}
            >
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="page-tabs">
          {[
            { key: 'client', label: '👤 Client' },
            { key: 'commande', label: '🚗 Commande' },
            { key: 'documents', label: '📎 Documents' },
            { key: 'tarifs', label: '💰 Tarifs' },
            { key: 'reprise', label: '🔄 Reprise' },
            { key: 'extras', label: '🎁 Extras' },
            { key: 'financement', label: '🏦 Financement' },
            { key: 'messagerie', label: '💬 Messagerie' },
            { key: 'decoupe', label: '✂️ Découpe documents' },
            ...(user.role === 'super-admin' ? [{ key: 'configuration', label: '⚙️ Configuration' }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              className={detailTab === tab.key ? 'active' : ''}
              onClick={() => setDetailTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="dc-detail-layout">
          {/* ── Contenu principal (gauche) ── */}
          <div className="dc-detail-main">
            {detailTab === 'client' && (
              <ClientTab dossier={dossier} onSave={handleSaveDossier} saving={saving} />
            )}
            {detailTab === 'commande' && (
              <CommandeTab dossier={dossier} onSave={handleSaveDossier} saving={saving} />
            )}
            {detailTab === 'documents' && (
              <DocumentsTab
                slots={slots}
                dossierId={selectedDossierId}
                onUpload={handleDocUpload}
                onValidate={handleDocValidate}
                onRefuse={(docId) => { setShowRefuseModal(docId); setRefuseComment(''); }}
                onDelete={handleDocDelete}
                onReload={() => loadSlots(selectedDossierId)}
              />
            )}
            {detailTab === 'tarifs' && (
              <TarifsTab dossier={dossier} onSave={handleSaveDossier} saving={saving} extras={extras} />
            )}
            {detailTab === 'reprise' && (
              <RepriseTab dossier={dossier} onSave={handleSaveDossier} saving={saving} />
            )}
            {detailTab === 'extras' && (
              <ExtrasTab dossierId={selectedDossierId} extras={extras} onReload={() => loadExtras(selectedDossierId)} />
            )}
            {detailTab === 'financement' && (
              <FinancementTab dossier={dossier} onSave={handleSaveDossier} saving={saving} />
            )}
            {detailTab === 'messagerie' && (
              <MessagerieTab
                dossierId={selectedDossierId}
                messages={messages}
                user={user}
                dossier={dossier}
                onReload={() => loadMessages(selectedDossierId)}
              />
            )}
            {detailTab === 'decoupe' && (
              <DecoupeTab
                dossierId={selectedDossierId}
                slots={slots}
                onReload={() => loadSlots(selectedDossierId)}
                onSelectionChange={setDecoupeSelectedPages}
                onSourceKeyChange={setDecoupeSourceKey}
                externalDroppedPages={decoupeDroppedPages}
              />
            )}
            {detailTab === 'configuration' && user.role === 'super-admin' && (
              <ConfigTab
                types={configTypes}
                onReload={loadConfigTypes}
                showModal={showConfigModal}
                setShowModal={setShowConfigModal}
                configForm={configForm}
                setConfigForm={setConfigForm}
                editingType={editingType}
                setEditingType={setEditingType}
              />
            )}

            {/* Historique inline */}
            {detailTab !== 'messagerie' && detailTab !== 'documents' && detailTab !== 'decoupe' && detailTab !== 'configuration' && (
              <div className="dc-form-section" style={{ marginTop: 20 }}>
                <div className="dc-form-section-title">Historique</div>
                {historique.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Aucune activité</p>
                ) : (
                  historique.slice(0, 10).map(h => (
                    <div key={h.id} className="dc-historique-item">
                      <span className="dc-historique-time">{formatDateTime(h.created_at)}</span>
                      <span className="dc-historique-user">{h.user_label}</span>
                      <span className="dc-historique-detail">{h.detail}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ── Sidebar documents (droite) ──
              ⚠ ABSENTE de l'onglet Configuration : on y règle les TYPES de
              documents, pas les pièces d'un dossier. Elle y prenait sa largeur
              et poussait la colonne « Actions » — donc le bouton Modifier —
              hors de l'écran. */}
          {detailTab !== 'configuration' && (
          <DocChecklist
            slots={slots}
            onNavigate={() => setDetailTab('documents')}
            dropEnabled={detailTab === 'decoupe'}
            onDropPages={detailTab === 'decoupe' ? async (slotId, pageNums) => {
              if (!decoupeSourceKey || !pageNums.length) return;
              try {
                const slot = slots.find(s => s.id === slotId);
                await dossiersCommerciaux.splitPdf(selectedDossierId, {
                  source_s3_key: decoupeSourceKey,
                  assignments: [{
                    pages: pageNums.map(p => p - 1),
                    type_document_id: slotId,
                    filename: `${slot?.nom || 'document'}.pdf`,
                  }],
                });
                loadSlots(selectedDossierId);
                setDecoupeDroppedPages(prev => [...prev, ...pageNums]);
                setDecoupeSelectedPages([]);
              } catch (e) {
                alert('Erreur: ' + (e.response?.data?.error || e.message));
              }
            } : null}
          />
          )}
        </div>

        {/* Refuse modal */}
        {showRefuseModal && (
          <div className="modal-overlay">
            <div className="modal-content small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Refuser le document</h2>
                <button className="modal-close" onClick={() => setShowRefuseModal(null)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="dc-form-field">
                  <label>Motif du refus</label>
                  <textarea
                    value={refuseComment}
                    onChange={e => setRefuseComment(e.target.value)}
                    rows={3}
                    placeholder="Indiquez la raison du refus..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowRefuseModal(null)}>Annuler</button>
                <button className="btn-primary" onClick={handleDocRefuse} style={{ background: 'var(--color-error)' }}>Refuser</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="page-container">
      <div className="page-tabs">
        <button
          className={activeTab === 'dossiers' ? 'active' : ''}
          onClick={() => setActiveTab('dossiers')}
        >📋 Dossiers</button>
        <button
          className={activeTab === 'livraisons' ? 'active' : ''}
          onClick={() => setActiveTab('livraisons')}
        >📅 Livraisons</button>
        <button
          className={activeTab === 'backoffice' ? 'active' : ''}
          onClick={() => setActiveTab('backoffice')}
        >🗃️ Back-office</button>
        {activeTab === 'dossiers' && Object.entries(STATUS_LABELS).map(([k, v]) => (
          <button
            key={k}
            className={filters.statut === k ? 'active' : ''}
            onClick={() => { setFilters(f => ({ ...f, statut: f.statut === k ? '' : k })); setPage(1); }}
          >
            {v} {stats[k] ? `(${stats[k]})` : ''}
          </button>
        ))}
      </div>

      {activeTab === 'livraisons' && (
        <LivraisonsTab onOpenDossier={openDossier} />
      )}

      {activeTab === 'backoffice' && (
        <BackOfficeTab onOpenDossier={openDossier} />
      )}

      {activeTab === 'dossiers' && <>
      <div className="page-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={filters.search}
          onChange={e => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
        />
        <select
          className={`page-select ${filters.vendeur_id ? 'active-filter' : ''}`}
          value={filters.vendeur_id}
          onChange={e => { setFilters(f => ({ ...f, vendeur_id: e.target.value })); setPage(1); }}
        >
          <option value="">Tous les vendeurs</option>
          {vendeurs.map(v => (
            <option key={v.id} value={v.id}>{v.prenom} {v.nom}</option>
          ))}
        </select>
        <select
          className={`page-select ${filters.type_vn_vo ? 'active-filter' : ''}`}
          value={filters.type_vn_vo}
          onChange={e => { setFilters(f => ({ ...f, type_vn_vo: e.target.value })); setPage(1); }}
        >
          <option value="">VN & VO</option>
          <option value="VN">VN</option>
          <option value="VO">VO</option>
        </select>
      </div>

      {editMode && user.role === 'super-admin' && selectedIds.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0' }}>
          <button className="btn-primary" style={{ background: 'var(--color-error)' }} onClick={handleDeleteDossiers}>
            🗑️ Supprimer la sélection ({selectedIds.length})
          </button>
        </div>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              {editMode && user.role === 'super-admin' && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === dossiers.length && dossiers.length > 0}
                    onChange={e => setSelectedIds(e.target.checked ? dossiers.map(d => d.id) : [])}
                  />
                </th>
              )}
              <th>N° Dossier</th>
              <th>Type</th>
              <th>Client</th>
              <th>Véhicule</th>
              <th>Vendeur</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Docs</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={editMode && user.role === 'super-admin' ? 9 : 8} style={{ textAlign: 'center', padding: 40 }}>Chargement...</td></tr>
            ) : dossiers.length === 0 ? (
              <tr><td colSpan={editMode && user.role === 'super-admin' ? 9 : 8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucun dossier</td></tr>
            ) : (
              dossiers.map(d => (
                <tr key={d.id} onClick={() => openDossier(d.id)} style={{ cursor: 'pointer' }}>
                  {editMode && user.role === 'super-admin' && (
                    <td onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(d.id)}
                        onChange={e => setSelectedIds(prev =>
                          e.target.checked ? [...prev, d.id] : prev.filter(x => x !== d.id)
                        )}
                      />
                    </td>
                  )}
                  <td style={{ fontWeight: 600 }}>{d.numero_dossier}</td>
                  <td><span style={{ fontWeight: 600, color: d.type_vn_vo === 'VN' ? 'var(--color-info)' : 'var(--color-success)' }}>{d.type_vn_vo}</span></td>
                  <td>{d.client_prenom} {d.client_nom}</td>
                  <td>{d.vehicule_marque} {d.vehicule_modele}</td>
                  <td>{d.vendeur_prenom} {d.vendeur_nom}</td>
                  <td>
                    <span className={STATUS_CLASSES[d.statut] || ''} style={{ padding: '2px 8px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 600 }}>
                      {STATUS_LABELS[d.statut]}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{formatDate(d.created_at)}</td>
                  <td>
                    <div className="dc-progress">
                      <div className="dc-progress-bar">
                        <div className="dc-progress-recu" style={{ width: d.docs_total > 0 ? `${((d.docs_fournis || 0) / d.docs_total) * 100}%` : '0%' }} />
                        <div className="dc-progress-fill" style={{ width: d.docs_total > 0 ? `${(d.docs_valides / d.docs_total) * 100}%` : '0%' }} />
                      </div>
                      <span className="dc-progress-text">{d.docs_valides || 0}/{d.docs_total || 0}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '12px 0' }}>
          <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span style={{ padding: '6px 12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Page {page} / {Math.ceil(total / LIMIT)}
          </span>
          <button className="btn-secondary" disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {/* New dossier modal */}
      {showNewModal && (
        <NewDossierModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreateDossier}
          user={user}
        />
      )}
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NewDossierModal
// ─────────────────────────────────────────────────────────────────────────────
function NewDossierModal({ onClose, onCreate, user }) {
  const [form, setForm] = useState({
    type_vn_vo: 'VN',
    client_nom: '', client_prenom: '', client_telephone: '', client_email: '',
    vehicule_marque: '', vehicule_modele: '',
    date_commande: '', codsoc: '', vendeur_id: '',
  });
  // Les sociétés sélectionnables : celles de la concession de l'utilisateur.
  // Une concession du CRM peut en regrouper plusieurs (CENTRAUTODIS = Autodis
  // + Centre Auto) et A97 BI ventile par société : sans ce choix, le bon de
  // commande n'est attribuable à aucune des deux.
  const [societes, setSocietes] = useState([]);
  // Le vendeur identifie le dossier : sans lui, aucune statistique de vente
  // n'est attribuable, et la commission n'a pas de destinataire.
  const [vendeursDispo, setVendeursDispo] = useState([]);
  const [lecture, setLecture] = useState(null);   // null | 'en cours' | { remplis, total, source }
  const [glisse, setGlisse] = useState(false);
  const [extraits, setExtraits] = useState(new Set());
  const fichierRef = useRef(null);
  useEffect(() => {
    dossiersCommerciaux.getVendeursAssignables()
      .then((r) => {
        const l = r.data.vendeurs || [];
        setVendeursDispo(l);
        // Un vendeur qui crée son propre dossier n'a pas à se désigner.
        if (user && l.some((v) => v.id === user.id)) setForm((f) => ({ ...f, vendeur_id: user.id }));
      })
      .catch(() => {});
    dossiersCommerciaux.societes()
      .then((r) => {
        const l = (r.data.societes || []).filter((x) => !user?.concession_id || x.concession_id === user.concession_id);
        setSocietes(l);
        if (l.length === 1) setForm((f) => ({ ...f, codsoc: l[0].codsoc }));
      })
      .catch(() => {});
  }, [user]);

  // Le bon de commande remplit le formulaire ; il ne l'enregistre pas.
  const lireLeBon = async (fichier) => {
    if (!fichier) return;
    setLecture('en cours');
    try {
      const { data } = await dossiersCommerciaux.extraire(fichier);
      const maj = {}; const vus = new Set();
      Object.entries(data.champs || {}).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        maj[k] = v; vus.add(k);
      });
      setForm((f) => ({ ...f, ...maj }));
      setExtraits(vus);
      setLecture({ remplis: data.remplis, total: data.total, source: data.source });
    } catch (e) {
      setLecture(null);
      alert(e.response?.data?.error || "Le bon de commande n'a pas pu être lu");
    }
  };

  const handleSubmit = () => {
    if (!form.type_vn_vo) return alert('Sélectionnez VN ou VO');
    // ⚠ Ces deux champs conditionnent la remontée vers A97 BI : un dossier sans
    // société ni date de signature reste invisible du décisionnel. Autant le
    // dire à la saisie plutôt que de laisser chercher plus tard.
    if (!form.vendeur_id) return alert("Identifiez le vendeur : le dossier ne peut pas être créé sans lui.");
    if (!form.codsoc) return alert('Sélectionnez la société : sans elle, le bon de commande ne remontera pas dans le BI.');
    if (!form.date_commande) return alert('Indiquez la date de signature du bon de commande.');
    onCreate(form);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Nouveau dossier commercial</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* La lecture du bon vient EN PREMIER : c'est le geste normal, la
              saisie manuelle devient le repli. */}
          {/* Même vocabulaire visuel que Litiges : `drop-zone` et
              `drop-zone-content` viennent de Litiges.css, importée plus haut.
              Dupliquer ces styles, c'est se condamner à les voir diverger. */}
          <div className="dc-depot-ligne">
            <div className={`drop-zone${glisse ? ' dragging' : ''}`}
              title="Glisser-déposer le bon de commande, ou cliquer"
              onClick={() => lecture !== 'en cours' && fichierRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setGlisse(true); }}
              onDragLeave={() => setGlisse(false)}
              onDrop={(e) => { e.preventDefault(); setGlisse(false); if (lecture !== 'en cours') lireLeBon(e.dataTransfer.files?.[0]); }}>
              <input ref={fichierRef} type="file" accept="application/pdf,image/*"
                style={{ display: 'none' }}
                onChange={(e) => { lireLeBon(e.target.files?.[0]); e.target.value = ''; }} />
              <div className="drop-zone-content">
                <span className="drop-icon">{lecture === 'en cours' ? '⏳' : '📤'}</span>
                <span>{lecture === 'en cours' ? <>Lecture…</> : <>Glisser-déposer<br />ou cliquer</>}</span>
              </div>
            </div>
            <div className="dc-depot-texte">
              {lecture && lecture !== 'en cours' ? (
                <>
                  <b className="dc-depot-ok">✅ {lecture.remplis} champ{lecture.remplis > 1 ? 's' : ''} sur {lecture.total} repris du bon</b>
                  <small>Relisez-les avant d'enregistrer — les champs repris sont encadrés en vert.</small>
                </>
              ) : (
                <>
                  <b>Déposer le bon de commande</b>
                  <small>PDF ou photo — les champs se remplissent tout seuls.</small>
                </>
              )}
            </div>
          </div>

          <div className="dc-form-grid">
            <div className={`dc-form-field${extraits.has('type_vn_vo') ? ' dc-extrait' : ''}`}>
              <label>Type *</label>
              <select value={form.type_vn_vo} onChange={e => setForm(f => ({ ...f, type_vn_vo: e.target.value }))}>
                <option value="VN">VN — Véhicule Neuf</option>
                <option value="VO">VO — Véhicule d'Occasion</option>
              </select>
            </div>
            <div className="dc-form-field">
              <label>Vendeur *</label>
              <select value={form.vendeur_id} onChange={e => setForm(f => ({ ...f, vendeur_id: e.target.value ? Number(e.target.value) : '' }))}>
                <option value="">— identifier le vendeur —</option>
                {vendeursDispo.map((v) => <option key={v.id} value={v.id}>{v.prenom} {v.nom}</option>)}
              </select>
            </div>
            <div className="dc-form-field">
              <label>Société *</label>
              <select value={form.codsoc} onChange={e => setForm(f => ({ ...f, codsoc: e.target.value }))}>
                <option value="">— choisir —</option>
                {societes.map((x) => <option key={x.codsoc} value={x.codsoc}>{x.nom}</option>)}
              </select>
            </div>
            <div className="dc-form-field">
              <label>Date de signature du bon *</label>
              <input type="date" value={form.date_commande}
                onChange={e => setForm(f => ({ ...f, date_commande: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Nom client</label>
              <input value={form.client_nom} onChange={e => setForm(f => ({ ...f, client_nom: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Prénom client</label>
              <input value={form.client_prenom} onChange={e => setForm(f => ({ ...f, client_prenom: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Téléphone</label>
              <input value={form.client_telephone} onChange={e => setForm(f => ({ ...f, client_telephone: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Email</label>
              <input type="email" value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Marque véhicule</label>
              <input value={form.vehicule_marque} onChange={e => setForm(f => ({ ...f, vehicule_marque: e.target.value }))} />
            </div>
            <div className="dc-form-field">
              <label>Modèle véhicule</label>
              <input value={form.vehicule_modele} onChange={e => setForm(f => ({ ...f, vehicule_modele: e.target.value }))} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSubmit}>Créer le dossier</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FormTab — generic form tab for Client, Commande, Reprise, Financement
// ─────────────────────────────────────────────────────────────────────────────
function FormTab({ title, fields, dossier, onSave, saving }) {
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (dossier) {
      const initial = {};
      fields.forEach(f => { initial[f.key] = dossier[f.key] || ''; });
      setForm(initial);
      setDirty(false);
    }
  }, [dossier, fields]);

  const handleChange = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave(form);
    setDirty(false);
  };

  return (
    <div className="dc-form-section">
      <div className="dc-form-section-title">{title}</div>
      <div className="dc-form-grid">
        {fields.map(f => (
          <div key={f.key} className={`dc-form-field ${f.fullWidth ? 'full-width' : ''}`}>
            <label>{f.label}{f.required ? ' *' : ''}</label>
            {f.type === 'select' ? (
              <select value={form[f.key] || ''} onChange={e => handleChange(f.key, e.target.value)}>
                <option value="">—</option>
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : f.type === 'textarea' ? (
              <textarea value={form[f.key] || ''} onChange={e => handleChange(f.key, e.target.value)} rows={3} />
            ) : (
              <input
                type={f.type || 'text'}
                value={form[f.key] || ''}
                onChange={e => handleChange(f.key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>
      {dirty && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Client Tab ──
const CLIENT_FIELDS = [
  { key: 'client_civilite', label: 'Civilité', type: 'select', options: [
    { value: 'M.', label: 'M.' }, { value: 'Mme', label: 'Mme' }, { value: 'Société', label: 'Société' }
  ]},
  { key: 'client_nom', label: 'Nom', required: true },
  { key: 'client_prenom', label: 'Prénom' },
  { key: 'client_telephone', label: 'Téléphone' },
  { key: 'client_email', label: 'Email', type: 'email' },
  { key: 'client_societe', label: 'Société' },
  { key: 'client_adresse', label: 'Adresse', fullWidth: true },
  { key: 'client_code_postal', label: 'Code postal' },
  { key: 'client_ville', label: 'Ville' },
  // Juste après l'adresse, parce que c'est là que la question se pose.
  //
  // ⚠ C'est le SEUL fait dont dépend une pièce conditionnelle et que le dossier
  // ne porte pas déjà : la reprise, l'entreprise et le financement se déduisent
  // de leurs blocs respectifs. Les redemander par un bouton Oui/Non créerait
  // deux sources pour un même fait, donc la possibilité qu'elles se
  // contredisent — et le vendeur saisirait deux fois la même chose.
  { key: 'situation_logement', label: 'Situation de logement', type: 'select', options: [
    { value: 'proprietaire', label: 'Propriétaire' },
    { value: 'locataire', label: 'Locataire' },
    { value: 'heberge', label: 'Hébergé chez un tiers' },
  ]},
];

function ClientTab({ dossier, onSave, saving }) {
  return <FormTab title="Informations client" fields={CLIENT_FIELDS} dossier={dossier} onSave={onSave} saving={saving} />;
}

// ── Commande Tab ──
const COMMANDE_FIELDS = [
  { key: 'type_vn_vo', label: 'Type', type: 'select', options: [
    { value: 'VN', label: 'VN — Véhicule Neuf' }, { value: 'VO', label: 'VO — Véhicule d\'Occasion' }
  ]},
  { key: 'vehicule_marque', label: 'Marque' },
  { key: 'vehicule_modele', label: 'Modèle' },
  { key: 'vehicule_version', label: 'Version / Finition' },
  { key: 'vehicule_couleur', label: 'Couleur' },
  { key: 'vehicule_immatriculation', label: 'Immatriculation' },
  { key: 'vehicule_vin', label: 'N° VIN / Châssis' },
  { key: 'vehicule_date_mise_circulation', label: 'Date mise en circulation', type: 'date' },
  { key: 'date_commande', label: 'Date de commande', type: 'date' },
  { key: 'date_livraison_prevue', label: 'Date livraison prévue', type: 'date' },
  { key: 'date_livraison_reelle', label: 'Date livraison réelle', type: 'date' },
  { key: 'numero_commande_constructeur', label: 'N° commande constructeur' },
];

function CommandeTab({ dossier, onSave, saving }) {
  return <FormTab title="Véhicule & Commande" fields={COMMANDE_FIELDS} dossier={dossier} onSave={onSave} saving={saving} />;
}

// ── Reprise Tab ──
const REPRISE_FIELDS = [
  { key: 'reprise_marque', label: 'Marque' },
  { key: 'reprise_modele', label: 'Modèle' },
  { key: 'reprise_immatriculation', label: 'Immatriculation' },
  { key: 'reprise_vin', label: 'N° VIN' },
  { key: 'reprise_km', label: 'Kilométrage', type: 'number' },
  { key: 'reprise_prix', label: 'Prix de reprise (€)', type: 'number' },
  { key: 'reprise_date_achat', label: 'Date d\'achat', type: 'date' },
];

function RepriseTab({ dossier, onSave, saving }) {
  return <FormTab title="Véhicule de reprise" fields={REPRISE_FIELDS} dossier={dossier} onSave={onSave} saving={saving} />;
}

// ── Financement Tab ──
const FINANCEMENT_FIELDS = [
  { key: 'financement_type', label: 'Type de financement', type: 'select', options: [
    { value: 'comptant', label: 'Comptant' },
    { value: 'credit', label: 'Crédit' },
    { value: 'lld', label: 'LLD' },
    { value: 'loa', label: 'LOA' },
  ]},
  { key: 'financement_organisme', label: 'Organisme financier' },
  { key: 'financement_montant', label: 'Montant financé (€)', type: 'number' },
  { key: 'financement_apport', label: 'Apport (€)', type: 'number' },
  { key: 'financement_mensualite', label: 'Mensualité (€)', type: 'number' },
  { key: 'financement_duree', label: 'Durée (mois)', type: 'number' },
  { key: 'financement_taux', label: 'Taux (%)', type: 'number' },
];

function FinancementTab({ dossier, onSave, saving }) {
  return <FormTab title="Financement" fields={FINANCEMENT_FIELDS} dossier={dossier} onSave={onSave} saving={saving} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// TarifsTab
// ─────────────────────────────────────────────────────────────────────────────
function TarifsTab({ dossier, onSave, saving, extras }) {
  const TARIFS_FIELDS = [
    { key: 'prix_catalogue', label: 'Prix catalogue (€)', type: 'number' },
    { key: 'remise_montant', label: 'Remise (€)', type: 'number' },
    { key: 'remise_pourcentage', label: 'Remise (%)', type: 'number' },
    { key: 'frais_mise_route', label: 'Frais mise en route (€)', type: 'number' },
    { key: 'frais_carte_grise', label: 'Frais carte grise (€)', type: 'number' },
    { key: 'frais_divers', label: 'Frais divers (€)', type: 'number' },
  ];

  const prixCatalogue = parseFloat(dossier?.prix_catalogue) || 0;
  const remise = parseFloat(dossier?.remise_montant) || 0;
  const prixNet = prixCatalogue - remise;
  const fraisMiseRoute = parseFloat(dossier?.frais_mise_route) || 0;
  const fraisCarteGrise = parseFloat(dossier?.frais_carte_grise) || 0;
  const fraisDivers = parseFloat(dossier?.frais_divers) || 0;
  const totalExtras = extras.reduce((sum, e) => sum + (parseFloat(e.prix) || 0), 0);
  const reprisePrix = parseFloat(dossier?.reprise_prix) || 0;
  const totalTTC = prixNet + fraisMiseRoute + fraisCarteGrise + fraisDivers + totalExtras - reprisePrix;

  return (
    <>
      <FormTab title="Tarification" fields={TARIFS_FIELDS} dossier={dossier} onSave={(data) => onSave({ ...data, prix_net: prixNet, total_ttc: totalTTC })} saving={saving} />
      <div className="dc-tarifs-summary">
        <div className="dc-tarifs-row"><span>Prix catalogue</span><span>{formatMoney(prixCatalogue)}</span></div>
        <div className="dc-tarifs-row"><span>Remise</span><span style={{ color: 'var(--color-error)' }}>- {formatMoney(remise)}</span></div>
        <div className="dc-tarifs-row"><span>Prix net</span><span>{formatMoney(prixNet)}</span></div>
        <div className="dc-tarifs-row"><span>Frais mise en route</span><span>{formatMoney(fraisMiseRoute)}</span></div>
        <div className="dc-tarifs-row"><span>Frais carte grise</span><span>{formatMoney(fraisCarteGrise)}</span></div>
        <div className="dc-tarifs-row"><span>Frais divers</span><span>{formatMoney(fraisDivers)}</span></div>
        {totalExtras > 0 && <div className="dc-tarifs-row"><span>Extras</span><span>{formatMoney(totalExtras)}</span></div>}
        {reprisePrix > 0 && <div className="dc-tarifs-row"><span>Reprise</span><span style={{ color: 'var(--color-success)' }}>- {formatMoney(reprisePrix)}</span></div>}
        <div className="dc-tarifs-row total"><span>Total TTC</span><span>{formatMoney(totalTTC)}</span></div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DocumentsTab
// ─────────────────────────────────────────────────────────────────────────────
function DocumentsTab({ slots, dossierId, onUpload, onValidate, onRefuse, onDelete, onReload }) {
  const fileInputRefs = useRef({});
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [expandedIa, setExpandedIa] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // doc inline preview

  const handleDrop = (e, typeId) => {
    e.preventDefault();
    setDragOverSlot(null);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file, typeId);
  };

  const handleFileSelect = (e, typeId) => {
    const file = e.target.files[0];
    if (file) onUpload(file, typeId);
    e.target.value = '';
  };

  const handleReanalyze = async (docId) => {
    try {
      await dossiersCommerciaux.analyzeDocument(dossierId, docId);
      setTimeout(() => onReload(), 3000);
      setTimeout(() => onReload(), 8000);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  useEffect(() => {
    const pending = slots.some(s => s.documents?.some(d => d.s3_key && !d.ia_statut));
    if (!pending) return;
    const interval = setInterval(() => onReload(), 5000);
    return () => clearInterval(interval);
  }, [slots, onReload]);

  if (!slots.length) {
    return (
      <div className="dc-empty">
        <span className="dc-empty-icon">📎</span>
        <span>Aucun type de document applicable pour ce dossier</span>
      </div>
    );
  }

  return (
    <>
      <div className="dc-doc-grid">
        {slots.map(slot => {
          const doc = slot.documents && slot.documents[0];
          const statut = doc ? doc.statut : 'manquant';
          const slotClass = statut === 'valide' ? 'dc-doc-validated' : statut === 'refuse' ? 'dc-doc-refused' : statut === 'uploade' ? 'dc-doc-uploaded' : '';

          return (
            <div
              key={slot.id}
              className={`dc-doc-slot ${slotClass} ${dragOverSlot === slot.id ? 'drag-over' : ''} ${previewDoc?.id === doc?.id ? 'dc-doc-active' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOverSlot(slot.id); }}
              onDragLeave={() => setDragOverSlot(null)}
              onDrop={e => handleDrop(e, slot.id)}
            >
              <div className="dc-doc-slot-header">
                <span className="dc-doc-slot-title">
                  {slot.nom}
                  {slot.obligatoire && <span style={{ color: 'var(--color-error)', marginLeft: 4 }}>*</span>}
                </span>
                <span className={`dc-doc-slot-badge ${slot.categorie}`}>{slot.categorie}</span>
              </div>

              <div className="dc-doc-slot-status">
                <span className={`status-dot ${statut}`} />
                {DOC_STATUS_LABELS[statut]}
                {doc && doc.ia_statut === 'ok' && (
                  <span className="dc-ia-badge ok" title="IA : document conforme">✓ IA</span>
                )}
                {doc && doc.ia_statut === 'alerte' && (
                  <span className="dc-ia-badge alerte" title="IA : anomalie(s) détectée(s)" onClick={() => setExpandedIa(expandedIa === doc.id ? null : doc.id)}>⚠ IA</span>
                )}
                {doc && doc.ia_statut === 'erreur' && (
                  <span className="dc-ia-badge erreur" title="IA : erreur d'analyse">✕ IA</span>
                )}
                {doc && doc.s3_key && !doc.ia_statut && (
                  <span className="dc-ia-badge pending" title="Analyse IA en cours...">⏳ IA</span>
                )}
              </div>

              {doc ? (
                <>
                  <div className="dc-doc-thumbnail" onClick={() => setPreviewDoc(previewDoc?.id === doc.id ? null : { ...doc, name: doc.nom, type: doc.type_mime })} style={{ cursor: 'pointer' }}>
                    {doc.url && doc.type_mime?.includes('pdf') ? (
                      <PdfThumbnail url={doc.url} />
                    ) : doc.url && doc.type_mime?.startsWith('image/') ? (
                      <img src={doc.url} alt={doc.nom} className="dc-doc-thumb-img" />
                    ) : (
                      <div className="dc-doc-thumb-fallback">📄</div>
                    )}
                  </div>

                  <div className="dc-doc-compact-info">
                    <div className="dc-doc-preview-name">{doc.nom}</div>
                    <div className="dc-doc-preview-meta">{formatSize(doc.taille)} — {formatDateTime(doc.created_at)}</div>
                  </div>

                  {doc.ia_statut === 'alerte' && doc.ia_erreurs && (expandedIa === doc.id) && (
                    <div className="dc-ia-alerts">
                      {(Array.isArray(doc.ia_erreurs) ? doc.ia_erreurs : []).map((err, i) => (
                        <div key={i} className="dc-ia-alert-item">⚠ {err}</div>
                      ))}
                    </div>
                  )}
                  {doc.ia_statut === 'erreur' && doc.ia_erreurs && (
                    <div className="dc-ia-alerts erreur">
                      {(Array.isArray(doc.ia_erreurs) ? doc.ia_erreurs : []).map((err, i) => (
                        <div key={i} className="dc-ia-alert-item">{err}</div>
                      ))}
                    </div>
                  )}

                  {doc.commentaire_refus && (
                    <div className="dc-doc-refusal">Refusé : {doc.commentaire_refus}</div>
                  )}
                  <div className="dc-doc-actions">
                    {doc.url && (
                      <button className="btn-icon" title="Aperçu" onClick={() => setPreviewDoc(previewDoc?.id === doc.id ? null : { ...doc, name: doc.nom, type: doc.type_mime })}>👁️</button>
                    )}
                    {doc.url && (
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="btn-icon" title="Télécharger">📥</a>
                    )}
                    {doc.ia_statut && (
                      <button className="btn-icon" title="Relancer l'analyse IA" onClick={() => handleReanalyze(doc.id)}>🤖</button>
                    )}
                    {statut === 'uploade' && (
                      <>
                        <button className="btn-icon" title="Valider" onClick={() => onValidate(doc.id)}>✅</button>
                        <button className="btn-icon" title="Refuser" onClick={() => onRefuse(doc.id)}>❌</button>
                      </>
                    )}
                    {statut === 'refuse' && (
                      <div className="dc-doc-dropzone" onClick={() => fileInputRefs.current[slot.id]?.click()}>
                        <span>📎 Remplacer</span>
                        <input ref={el => fileInputRefs.current[slot.id] = el} type="file" style={{ display: 'none' }} onChange={e => handleFileSelect(e, slot.id)} />
                      </div>
                    )}
                    <button className="btn-icon delete" title="Supprimer" onClick={() => onDelete(doc.id)}>🗑️</button>
                  </div>
                </>
              ) : (
                <div className={`dc-doc-dropzone ${dragOverSlot === slot.id ? 'drag-over' : ''}`} onClick={() => fileInputRefs.current[slot.id]?.click()}>
                  <span style={{ fontSize: '1.5rem' }}>📎</span>
                  <span>Glisser ou cliquer pour uploader</span>
                  <input ref={el => fileInputRefs.current[slot.id] = el} type="file" style={{ display: 'none' }} onChange={e => handleFileSelect(e, slot.id)} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Overlay preview */}
      {previewDoc && previewDoc.url && (
        <div className="modal-overlay">
          <div className="dc-preview-overlay" onClick={e => e.stopPropagation()}>
            <div className="dc-preview-overlay-header">
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{previewDoc.name}</span>
              <button className="modal-close" onClick={() => setPreviewDoc(null)}>✕</button>
            </div>
            <div className="dc-preview-overlay-body">
              {previewDoc.type?.includes('pdf') ? (
                <embed src={previewDoc.url} type="application/pdf" style={{ width: '100%', height: '100%' }} />
              ) : previewDoc.type?.startsWith('image/') ? (
                <img src={previewDoc.url} alt={previewDoc.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              ) : (
                <div className="dc-empty"><span>Aperçu non disponible</span></div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ExtrasTab
// ─────────────────────────────────────────────────────────────────────────────
function ExtrasTab({ dossierId, extras, onReload }) {
  const [newLabel, setNewLabel] = useState('');
  const [newPrix, setNewPrix] = useState('');

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    try {
      await dossiersCommerciaux.addExtra(dossierId, { label: newLabel.trim(), prix: parseFloat(newPrix) || 0 });
      setNewLabel('');
      setNewPrix('');
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (extraId) => {
    try {
      await dossiersCommerciaux.deleteExtra(dossierId, extraId);
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const total = extras.reduce((s, e) => s + (parseFloat(e.prix) || 0), 0);

  return (
    <div className="dc-form-section">
      <div className="dc-form-section-title">Extras & Accessoires</div>
      <div className="dc-extras-list">
        {extras.map(e => (
          <div key={e.id} className="dc-extras-item">
            <span style={{ flex: 1 }}>{e.label}</span>
            <span style={{ fontWeight: 600, minWidth: 100, textAlign: 'right' }}>{formatMoney(e.prix)}</span>
            <button className="btn-icon delete" title="Supprimer" onClick={() => handleDelete(e.id)}>🗑️</button>
          </div>
        ))}
        <div className="dc-extras-item" style={{ background: 'var(--bg-muted)' }}>
          <input
            className="dc-extras-label"
            placeholder="Nouvel extra..."
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <input
            className="dc-extras-prix"
            type="number"
            placeholder="Prix"
            value={newPrix}
            onChange={e => setNewPrix(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button className="btn-icon" title="Ajouter" onClick={handleAdd}>➕</button>
        </div>
      </div>
      {extras.length > 0 && (
        <div style={{ textAlign: 'right', marginTop: 12, fontWeight: 700, fontSize: '1rem' }}>
          Total extras : {formatMoney(total)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LienClientBar — le lien que le client reçoit pour déposer ses pièces
// ─────────────────────────────────────────────────────────────────────────────
// ⚠ Le jeton n'existe en clair qu'à la seconde où il est créé : le serveur n'en
// garde que l'empreinte. On l'affiche donc une fois, avec un bouton de copie, et
// la seule façon d'en récupérer un est d'en régénérer un — ce qui tue l'ancien.
function LienClientBar({ dossierId, dossier, onTrace }) {
  const [etat, setEtat] = useState(null);      // lien en cours, ou null
  const [jeton, setJeton] = useState('');      // l'URL, visible une seule fois
  const [occupe, setOccupe] = useState(false);
  const [copie, setCopie] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await dossiersCommerciaux.lienClient(dossierId);
      setEtat(r.data.lien);
    } catch { /* l'absence de lien n'est pas une erreur */ }
  }, [dossierId]);
  useEffect(() => { setJeton(''); charger(); }, [charger]);

  const joignable = !!(dossier?.client_email || dossier?.client_telephone);

  const generer = async () => {
    if (etat && !window.confirm(
      "Régénérer le lien rendra l'ancien inutilisable. Le client devra utiliser le nouveau. Continuer ?"
    )) return;
    setOccupe(true);
    try {
      const r = await dossiersCommerciaux.genererLienClient(dossierId);
      setJeton(r.data.url);
      await charger();
      onTrace?.();
    } catch (e) {
      alert(e.response?.data?.error || "Le lien n'a pas pu être généré");
    }
    setOccupe(false);
  };

  const copier = async () => {
    try { await navigator.clipboard.writeText(jeton); setCopie(true); setTimeout(() => setCopie(false), 2000); }
    catch { window.prompt('Copiez le lien :', jeton); }
  };

  return (
    <div className="dc-lien-bar">
      <div className="dc-lien-t">
        <span>🔗 Lien client</span>
        {etat ? (
          <span className="dc-lien-meta">
            expire le {formatDate(etat.expire_le)}
            {' · '}{Number(etat.nb_ouvertures) || 0} ouverture{Number(etat.nb_ouvertures) > 1 ? 's' : ''}
            {' · '}{Number(etat.pieces_client) || 0} pièce{Number(etat.pieces_client) > 1 ? 's' : ''} déposée{Number(etat.pieces_client) > 1 ? 's' : ''}
          </span>
        ) : (
          <span className="dc-lien-meta">aucun lien actif</span>
        )}
        <button className="btn-secondary dc-lien-btn" onClick={generer}
          disabled={occupe || !joignable}
          title={joignable ? '' : "Ce client n'a ni e-mail ni téléphone"}>
          {occupe ? '…' : etat ? 'Régénérer' : 'Générer le lien'}
        </button>
      </div>
      {jeton && (
        <div className="dc-lien-url">
          <code>{jeton}</code>
          <button className="btn-secondary dc-lien-btn" onClick={copier}>{copie ? '✓ Copié' : 'Copier'}</button>
          <span className="dc-lien-note">
            Transmettez ce lien au client. Il ne sera plus affiché : le serveur n'en garde que l'empreinte.
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MessagerieTab
// ─────────────────────────────────────────────────────────────────────────────
function MessagerieTab({ dossierId, messages, user, onReload, dossier }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!text.trim() && !attachFile) return;
    setSending(true);
    try {
      if (attachFile) {
        const presignRes = await dossiersCommerciaux.presignMessage(dossierId, {
          filename: attachFile.name, contentType: attachFile.type,
        });
        const { uploadUrl, key } = presignRes.data;
        await fetch(uploadUrl, { method: 'PUT', body: attachFile, headers: { 'Content-Type': attachFile.type } });
        await dossiersCommerciaux.confirmMessage(dossierId, {
          contenu: text.trim(), key, filename: attachFile.name, contentType: attachFile.type, size: attachFile.size,
        });
      } else {
        await dossiersCommerciaux.sendMessage(dossierId, { contenu: text.trim() });
      }
      setText('');
      setAttachFile(null);
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setSending(false);
  };

  return (
    <div className="dc-chat-container">
      <LienClientBar dossierId={dossierId} dossier={dossier} onTrace={onReload} />
      <div className="dc-chat-messages">
        {messages.length === 0 ? (
          <div className="dc-empty" style={{ height: '100%' }}>
            <span className="dc-empty-icon">💬</span>
            <span>Aucun message</span>
          </div>
        ) : (
          messages.map(m => (
            <div key={m.id} className={`dc-chat-bubble ${m.user_id === user.id ? 'own' : 'other'}`}>
              {m.user_id !== user.id && (
                <div className="dc-chat-bubble-author">{m.user_prenom} {m.user_nom}</div>
              )}
              {m.contenu && <div>{m.contenu}</div>}
              {m.attachment_url && (
                <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="dc-chat-bubble-attachment">
                  📎 {m.attachment_name}
                </a>
              )}
              <div className="dc-chat-bubble-time">{formatDateTime(m.created_at)}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="dc-chat-input-bar">
        <button
          className="btn-icon"
          title="Joindre un fichier"
          onClick={() => fileInputRef.current?.click()}
          style={{ fontSize: '1.2rem' }}
        >
          📎
        </button>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={e => { setAttachFile(e.target.files[0]); e.target.value = ''; }}
        />
        {attachFile && (
          <span style={{ fontSize: '0.8rem', color: 'var(--color-info)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attachFile.name}
            <button onClick={() => setAttachFile(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-error)', marginLeft: 4 }}>✕</button>
          </span>
        )}
        <textarea
          className="dc-chat-input"
          placeholder="Écrire un message..."
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          rows={1}
        />
        <button className="dc-chat-send" onClick={handleSend} disabled={sending || (!text.trim() && !attachFile)}>
          Envoyer
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DecoupeTab — PDF Split & Merge
// ─────────────────────────────────────────────────────────────────────────────
function DecoupeTab({ dossierId, slots, onReload, onSelectionChange, onSourceKeyChange, externalDroppedPages }) {
  const [sourceKey, setSourceKey] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [assignTypeId, setAssignTypeId] = useState('');
  const [splitting, setSplitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  const [assignedPages, setAssignedPages] = useState([]); // pages déjà envoyées vers Documents
  const canvasRefs = useRef({});
  const allAssigned = [...assignedPages, ...(externalDroppedPages || [])];

  // Expose selection and sourceKey to parent for sidebar drop
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedPages);
  }, [selectedPages, onSelectionChange]);

  useEffect(() => {
    if (onSourceKeyChange) onSourceKeyChange(sourceKey);
  }, [sourceKey, onSourceKeyChange]);

  const handleSourceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.includes('pdf')) {
      alert('Veuillez sélectionner un fichier PDF');
      return;
    }
    setUploading(true);
    setAutoResult(null);
    setAssignedPages([]);

    try {
      const presignRes = await dossiersCommerciaux.presignDocument(dossierId, {
        filename: file.name, contentType: file.type,
      });
      const { uploadUrl, key } = presignRes.data;
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setSourceKey(key);

      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pagesArr = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.2 });
        pagesArr.push({ pageNum: i, width: viewport.width, height: viewport.height, page });
      }
      setPages(pagesArr);
      setSelectedPages([]);

      setTimeout(() => {
        pagesArr.forEach(async (p) => {
          const canvas = canvasRefs.current[p.pageNum];
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          const viewport = p.page.getViewport({ scale: 1.2 });
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await p.page.render({ canvasContext: ctx, viewport }).promise;
        });
      }, 100);
    } catch (err) {
      console.error('Erreur lecture PDF:', err);
      alert('Erreur lors de la lecture du PDF');
    }
    setUploading(false);
  };

  const togglePage = (pageNum) => {
    if (allAssigned.includes(pageNum)) return;
    setSelectedPages(prev =>
      prev.includes(pageNum) ? prev.filter(p => p !== pageNum) : [...prev, pageNum]
    );
  };

  // Déplacer les pages sélectionnées vers un slot (Documents)
  const handleMoveToSlot = async () => {
    if (!selectedPages.length || !assignTypeId) {
      alert('Sélectionnez des pages et un type de document');
      return;
    }
    setSplitting(true);
    try {
      await dossiersCommerciaux.splitPdf(dossierId, {
        source_s3_key: sourceKey,
        assignments: [{
          pages: selectedPages.map(p => p - 1),
          type_document_id: parseInt(assignTypeId),
          filename: `${slots.find(s => s.id === parseInt(assignTypeId))?.nom || 'split'}.pdf`,
        }],
      });
      // Marquer les pages comme assignées
      setAssignedPages(prev => [...prev, ...selectedPages]);
      setSelectedPages([]);
      setAssignTypeId('');
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setSplitting(false);
  };

  // Tri automatique IA
  const handleAutoAnalyze = async () => {
    if (!sourceKey) return;
    setAnalyzing(true);
    setAutoResult(null);
    try {
      const res = await dossiersCommerciaux.autoSplitAnalyze(dossierId, { source_s3_key: sourceKey });
      setAutoResult(res.data);
    } catch (e) {
      alert('Erreur analyse IA: ' + (e.response?.data?.error || e.message));
    }
    setAnalyzing(false);
  };

  const updateAutoAssignment = (idx, typeId) => {
    setAutoResult(prev => {
      const updated = { ...prev, assignments: [...prev.assignments] };
      const slot = slots.find(s => s.id === parseInt(typeId));
      updated.assignments[idx] = {
        ...updated.assignments[idx],
        type_document_id: typeId ? parseInt(typeId) : null,
        type_document_nom: slot ? slot.nom : null,
      };
      return updated;
    });
  };

  const handleAutoConfirm = async () => {
    if (!autoResult?.assignments?.length) return;
    const validAssignments = autoResult.assignments.filter(a => a.type_document_id);
    if (validAssignments.length === 0) {
      alert('Aucun document assigné. Corrigez les types avant de valider.');
      return;
    }
    setSplitting(true);
    try {
      await dossiersCommerciaux.splitPdf(dossierId, {
        source_s3_key: sourceKey,
        assignments: validAssignments.map(a => ({
          pages: a.pages,
          type_document_id: a.type_document_id,
          filename: `${a.type_document_nom || 'document'}.pdf`,
        })),
      });
      alert(`${validAssignments.length} document(s) créé(s) avec succès !`);
      // Marquer toutes les pages comme assignées
      const allAssignedPages = validAssignments.flatMap(a => a.pages.map(p => p + 1));
      setAssignedPages(prev => [...prev, ...allAssignedPages]);
      setAutoResult(null);
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setSplitting(false);
  };

  const formatPageRange = (pagesArr) => {
    if (!pagesArr || !pagesArr.length) return '';
    const display = pagesArr.map(p => p + 1);
    if (display.length === 1) return `Page ${display[0]}`;
    return `Pages ${display[0]}–${display[display.length - 1]}`;
  };

  const CONFIDENCE_BADGES = {
    high: { label: 'Confiance élevée', className: 'dc-confidence-high' },
    medium: { label: 'Confiance moyenne', className: 'dc-confidence-medium' },
    low: { label: 'Confiance faible', className: 'dc-confidence-low' },
  };

  const availablePages = pages.filter(p => !allAssigned.includes(p.pageNum));

  return (
    <div className="dc-split-container">
      <div className="dc-form-section">
        <div className="dc-form-section-title">✂️ Découpe documents</div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 12 }}>
          Uploadez un PDF multi-pages. Utilisez le <strong>tri automatique IA</strong> ou sélectionnez les pages manuellement pour les déplacer vers Documents.
        </p>
        <input type="file" accept="application/pdf" onChange={handleSourceUpload} disabled={uploading} />
        {uploading && <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>Chargement du PDF...</p>}
      </div>

      {pages.length > 0 && !autoResult && (
        <>
          <div className="dc-form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="dc-form-section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                Pages disponibles ({availablePages.length}/{pages.length})
                {assignedPages.length > 0 && (
                  <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--color-success)', marginLeft: 8 }}>
                    ✓ {assignedPages.length} déplacée(s)
                  </span>
                )}
              </div>
              <button
                className="btn-primary"
                onClick={handleAutoAnalyze}
                disabled={analyzing || availablePages.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {analyzing ? (
                  <><span style={{ animation: 'dc-pulse 1s infinite' }}>🤖</span> Analyse en cours...</>
                ) : (
                  <>🤖 Tri automatique IA</>
                )}
              </button>
            </div>

            {/* Barre d'assignation rapide (visible quand pages sélectionnées) */}
            {selectedPages.length > 0 && (
              <div className="dc-split-assign-bar">
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {selectedPages.length} page(s) sélectionnée(s) →
                </span>
                <select
                  className="page-select"
                  value={assignTypeId}
                  onChange={e => setAssignTypeId(e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Déplacer vers...</option>
                  {slots.map(s => (
                    <option key={s.id} value={s.id}>{s.nom} ({s.categorie})</option>
                  ))}
                </select>
                <button className="btn-primary" onClick={handleMoveToSlot} disabled={splitting || !assignTypeId}>
                  {splitting ? '...' : '📎 Déplacer'}
                </button>
              </div>
            )}

            <div className="dc-split-pages">
              {pages.map(p => {
                const isAssigned = allAssigned.includes(p.pageNum);
                const isSelected = selectedPages.includes(p.pageNum);
                return (
                  <div
                    key={p.pageNum}
                    className={`dc-split-page ${isSelected ? 'selected' : ''} ${isAssigned ? 'assigned' : ''}`}
                    onClick={() => togglePage(p.pageNum)}
                    draggable={!isAssigned}
                    onDragStart={(e) => {
                      // Drag la page courante + toutes les pages sélectionnées
                      const pagesToDrag = isSelected ? selectedPages : [p.pageNum];
                      e.dataTransfer.setData('application/x-dc-pages', JSON.stringify(pagesToDrag));
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                  >
                    <canvas ref={el => canvasRefs.current[p.pageNum] = el} />
                    <div className="dc-split-page-number">Page {p.pageNum}</div>
                    {isSelected && <div className="dc-split-page-check">✓</div>}
                    {isAssigned && <div className="dc-split-page-assigned">✓ Déplacé</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── Résultat tri automatique IA ── */}
      {autoResult && (
        <div className="dc-form-section dc-autosplit-review">
          <div className="dc-form-section-title">
            🤖 Tri automatique — {autoResult.total_pages} pages → {autoResult.assignments.length} document(s)
          </div>

          <div className="dc-autosplit-list">
            {autoResult.assignments.map((a, idx) => {
              const assignmentPages = a.pages || [];
              const conf = CONFIDENCE_BADGES[a.confidence] || CONFIDENCE_BADGES.low;
              return (
                <div key={idx} className={`dc-autosplit-item ${a.type_document_id ? '' : 'unassigned'}`}>
                  <div className="dc-autosplit-thumbs">
                    {assignmentPages.map(pageIdx => {
                      const pageNum = pageIdx + 1;
                      return (
                        <div key={pageIdx} className="dc-autosplit-thumb-page">
                          {canvasRefs.current[pageNum] ? (
                            <canvas
                              ref={el => {
                                if (!el || !canvasRefs.current[pageNum]) return;
                                const src = canvasRefs.current[pageNum];
                                el.width = src.width;
                                el.height = src.height;
                                el.getContext('2d').drawImage(src, 0, 0);
                              }}
                            />
                          ) : (
                            <span style={{ fontSize: '1rem' }}>📄</span>
                          )}
                          <span className="dc-autosplit-thumb-label">{pageNum}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="dc-autosplit-info">
                    <div className="dc-autosplit-pages">{formatPageRange(a.pages)}</div>
                    <span className={`dc-autosplit-confidence ${conf.className}`}>{conf.label}</span>
                    {a.description && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{a.description}</span>}
                  </div>
                  <select
                    className="page-select"
                    value={a.type_document_id || ''}
                    onChange={e => updateAutoAssignment(idx, e.target.value)}
                    style={{ minWidth: 200 }}
                  >
                    <option value="">— Non assigné —</option>
                    {slots.map(s => (
                      <option key={s.id} value={s.id}>{s.nom} ({s.categorie})</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn-secondary" onClick={() => setAutoResult(null)}>Annuler</button>
            <button
              className="btn-primary"
              onClick={handleAutoConfirm}
              disabled={splitting || !autoResult.assignments.some(a => a.type_document_id)}
            >
              {splitting ? 'Découpe en cours...' : `✅ Valider le tri (${autoResult.assignments.filter(a => a.type_document_id).length} documents)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ConfigTab — Super-admin document types management
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// EditeurConditions — quand ce document est-il demandé ?
// ─────────────────────────────────────────────────────────────────────────────
// Sans condition, la pièce est demandée sur TOUS les dossiers. Avec, elle
// n'apparaît que si le dossier remplit CHACUNE des conditions.
//
// ⚠ Les champs proposés sont ceux que le dossier porte DÉJÀ : on ne demande
// jamais au vendeur de redire ce qui se déduit. « Il y a une reprise » se lit
// dans le bloc Reprise, « c'est une entreprise » dans la raison sociale. Seule
// la situation de logement est une vraie question, parce que rien ne l'implique.
// ⚠ `valeurs` n'est pas un confort : sans elle, le champ de saisie était LIBRE
// et il fallait deviner la valeur STOCKÉE. Constaté en production le
// 2026-09-20 : la condition avait été écrite « != Hébergé » quand la base
// stocke « heberge » — elle ne se déclenchait donc jamais, en silence.
const CHAMPS_CONDITION = [
  { v: 'situation_logement', l: 'Situation de logement', valeurs: [
    { v: 'proprietaire', l: 'Propriétaire' },
    { v: 'locataire', l: 'Locataire' },
    { v: 'heberge', l: 'Hébergé chez un tiers' },
  ]},
  { v: 'client_societe', l: 'Raison sociale du client' },
  { v: 'reprise_immatriculation', l: 'Immatriculation de la reprise' },
  { v: 'financement_type', l: 'Type de financement' },
  { v: 'type_vn_vo', l: 'VN / VO', valeurs: [
    { v: 'VN', l: 'VN — Véhicule neuf' },
    { v: 'VO', l: "VO — Véhicule d'occasion" },
  ]},
];
const champDe = (v) => CHAMPS_CONDITION.find((c) => c.v === v) || {};
// Affiche le LIBELLÉ de la valeur stockée : « Hébergé chez un tiers », pas
// « heberge ». Une règle qu'on ne sait pas relire est une règle qu'on n'ose
// plus modifier.
const libelleValeur = (champ, valeur) => {
  const l = (champDe(champ).valeurs || []).find((x) => x.v === valeur);
  return l ? l.l : (valeur || '');
};
const OPERATEURS_CONDITION = [
  { v: 'rempli', l: 'est renseigné' },
  { v: 'vide', l: "n'est pas renseigné" },
  { v: '=', l: 'est égal à' },
  { v: '!=', l: 'est différent de' },
  { v: 'IN', l: 'est parmi (virgules)' },
];
const SANS_VALEUR = ['rempli', 'vide'];
const libelleDe = (liste, v) => (liste.find((x) => x.v === v) || {}).l || v;

function EditeurConditions({ typeId, onReload }) {
  const [conditions, setConditions] = useState([]);
  const [form, setForm] = useState({ champ: 'situation_logement', operateur: 'rempli', valeur: '' });
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await dossiersCommerciaux.getConditions(typeId);
      setConditions(r.data.conditions || []);
    } catch { /* un type sans condition n'est pas une erreur */ }
  }, [typeId]);
  useEffect(() => { charger(); }, [charger]);

  const sansValeur = SANS_VALEUR.includes(form.operateur);

  const ajouter = async () => {
    if (!sansValeur && !form.valeur.trim()) {
      return alert('Indiquez une valeur, ou choisissez « est renseigné ».');
    }
    setOccupe(true);
    try {
      await dossiersCommerciaux.addCondition(typeId, {
        champ: form.champ,
        operateur: form.operateur,
        valeur: sansValeur ? null : form.valeur.trim(),
      });
      setForm((f) => ({ ...f, valeur: '' }));
      await charger(); onReload?.();
    } catch (e) { alert(e.response?.data?.error || "La condition n'a pas pu être ajoutée"); }
    setOccupe(false);
  };

  const retirer = async (id) => {
    try { await dossiersCommerciaux.deleteCondition(id); await charger(); onReload?.(); }
    catch (e) { alert(e.response?.data?.error || 'Suppression impossible'); }
  };

  return (
    <>
      <label>Conditions d'affichage</label>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>
        {conditions.length === 0
          ? 'Aucune condition : ce document est demandé sur tous les dossiers.'
          : "Ce document n'est demandé que si TOUTES les conditions ci-dessous sont remplies."}
      </span>
      {conditions.map((c) => (
        <div key={c.id} className="dc-condition-ligne">
          <span>
            {libelleDe(CHAMPS_CONDITION, c.champ)} <b>{libelleDe(OPERATEURS_CONDITION, c.operateur)}</b> {libelleValeur(c.champ, c.valeur)}
          </span>
          <button className="btn-icon delete" title="Retirer" onClick={() => retirer(c.id)}>🗑️</button>
        </div>
      ))}
      <div className="dc-condition-ajout">
        <select value={form.champ} onChange={(e) => setForm((f) => ({ ...f, champ: e.target.value, valeur: '' }))}>
          {CHAMPS_CONDITION.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <select value={form.operateur} onChange={(e) => setForm((f) => ({ ...f, operateur: e.target.value }))}>
          {OPERATEURS_CONDITION.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        {!sansValeur && (champDe(form.champ).valeurs
          ? (
            <select value={form.valeur} onChange={(e) => setForm((f) => ({ ...f, valeur: e.target.value }))}>
              <option value="">— choisir —</option>
              {champDe(form.champ).valeurs.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
          ) : (
            <input value={form.valeur} placeholder="valeur"
              onChange={(e) => setForm((f) => ({ ...f, valeur: e.target.value }))} />
          ))}
        <button className="btn-secondary" disabled={occupe} onClick={ajouter}>Ajouter</button>
      </div>
    </>
  );
}

function ConfigTab({ types, onReload, showModal, setShowModal, configForm, setConfigForm, editingType, setEditingType }) {
  const handleSave = async () => {
    try {
      if (editingType) {
        await dossiersCommerciaux.updateTypeDocument(editingType.id, configForm);
      } else {
        await dossiersCommerciaux.createTypeDocument(configForm);
      }
      setShowModal(false);
      setEditingType(null);
      setConfigForm({ nom: '', categorie: 'vendeur', obligatoire: false, ordre: 0, ia_prompt: '', rubrique: 'Dossier général' });
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Désactiver ce type de document ?')) return;
    try {
      await dossiersCommerciaux.deleteTypeDocument(id);
      onReload();
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
  };

  const openEdit = (type) => {
    setEditingType(type);
    setConfigForm({ nom: type.nom, categorie: type.categorie, obligatoire: type.obligatoire, ordre: type.ordre, ia_prompt: type.ia_prompt || '', rubrique: type.rubrique || 'Dossier général' });
    setShowModal(true);
  };

  const openNew = () => {
    setEditingType(null);
    setConfigForm({ nom: '', categorie: 'vendeur', obligatoire: false, ordre: 0, ia_prompt: '', rubrique: 'Dossier général' });
    setShowModal(true);
  };

  return (
    <div className="dc-form-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="dc-form-section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
          Types de documents
        </div>
        <button className="btn-primary" onClick={openNew}>+ Nouveau type</button>
      </div>

      <table className="dc-config-table">
        <thead>
          <tr>
            <th>Ordre</th>
            <th>Rubrique</th>
            <th>Nom</th>
            <th>Catégorie</th>
            <th>Obligatoire</th>
            <th>Actif</th>
            <th>Conditions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {types.map(t => (
            <tr key={t.id} style={{ opacity: t.actif ? 1 : 0.5 }}>
              <td>{t.ordre}</td>
              <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{t.rubrique || 'Dossier général'}</td>
              <td style={{ fontWeight: 600 }}>{t.nom}</td>
              <td><span className={`dc-doc-slot-badge ${t.categorie}`}>{t.categorie}</span></td>
              <td>{t.obligatoire ? '✅' : '—'}</td>
              <td>{t.actif ? '✅' : '❌'}</td>
                            <td>
                {t.conditions && t.conditions.length > 0 ? (
                  t.conditions.map(c => (
                    <span key={c.id} style={{ fontSize: '0.75rem', background: 'var(--bg-muted)', padding: '2px 6px', borderRadius: 4, marginRight: 4 }}>
                      {c.champ} {c.operateur} {c.valeur}
                    </span>
                  ))
                ) : '—'}
              </td>
              <td>
                <div className="action-buttons">
                  <button className="btn-icon" title="Modifier" onClick={() => openEdit(t)}>✏️</button>
                  <button className="btn-icon delete" title="Désactiver" onClick={() => handleDelete(t.id)}>🗑️</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingType ? 'Modifier le type' : 'Nouveau type de document'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="dc-form-grid">
                <div className="dc-form-field full-width">
                  <label>Nom *</label>
                  <input value={configForm.nom} onChange={e => setConfigForm(f => ({ ...f, nom: e.target.value }))} />
                </div>
                <div className="dc-form-field">
                  <label>Catégorie *</label>
                  <select value={configForm.categorie} onChange={e => setConfigForm(f => ({ ...f, categorie: e.target.value }))}>
                    <option value="vendeur">Vendeur</option>
                    <option value="secretaire">Secrétaire</option>
                  </select>
                </div>
                <div className="dc-form-field">
                  <label>Ordre</label>
                  <input type="number" value={configForm.ordre} onChange={e => setConfigForm(f => ({ ...f, ordre: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="dc-form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={configForm.obligatoire}
                      onChange={e => setConfigForm(f => ({ ...f, obligatoire: e.target.checked }))}
                    />
                    Obligatoire
                  </label>
                </div>
                <div className="dc-form-field full-width">
                  <label>Rubrique</label>
                  <input list="dc-rubriques" value={configForm.rubrique}
                    onChange={e => setConfigForm(f => ({ ...f, rubrique: e.target.value }))}
                    placeholder="Dossier général" />
                  {/* Liste LIBRE avec suggestions : une rubrique nouvelle se crée
                      en la tapant, sans passer par le code. */}
                  <datalist id="dc-rubriques">
                    {[...new Set([
                      ...types.map(t => (t.rubrique || '').trim()).filter(Boolean),
                      'Dossier général', 'Financement', 'Reprise',
                    ])].map(r => <option key={r} value={r} />)}
                  </datalist>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, marginBottom: 10 }}>
                    Regroupe la pièce dans la liste du dossier. Laissée vide, elle va dans « Dossier général ».
                  </span>
                  <label>Prompt IA — contrôles spécifiques</label>
                  <textarea
                    value={configForm.ia_prompt}
                    onChange={e => setConfigForm(f => ({ ...f, ia_prompt: e.target.value }))}
                    rows={4}
                    placeholder="Ex: Vérifier que la CNI n'est pas expirée, que la photo est visible, que le nom est lisible..."
                    style={{ fontSize: '0.85rem' }}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    Instructions envoyées à l'IA pour vérifier la conformité de ce type de document.
                  </span>
                </div>
                  {editingType && (
                    <div className="dc-form-group full">
                      <EditeurConditions typeId={editingType.id} onReload={onReload} />
                    </div>
                  )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleSave}>{editingType ? 'Modifier' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LivraisonsTab — Planning livraisons par date
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// BackOfficeTab — le secrétariat reprend ce qui a été livré
// ─────────────────────────────────────────────────────────────────────────────
// Second temps du process : les chefs des ventes tiennent l'onglet Livraisons,
// le secrétariat travaille ici. Un dossier y entre dès qu'il est coché livré.
//
// ⚠ Aucune recopie : cette vue lit la MÊME ligne que Livraisons, filtrée sur le
// statut. Dupliquer les informations, c'est se donner deux versions du même
// véhicule et attendre qu'elles divergent.
function BackOfficeTab({ onOpenDossier }) {
  const [dossiers, setDossiers] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [chargement, setChargement] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await dossiersCommerciaux.backOffice({
        recherche: recherche || undefined, debut: debut || undefined, fin: fin || undefined,
      });
      setDossiers(r.data.dossiers || []);
    } catch (e) { console.error('Back-office :', e); }
    setChargement(false);
  }, [recherche, debut, fin]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);

  const modifier = async (id, champ, valeur) => {
    try {
      await dossiersCommerciaux.updateLivraison(id, { [champ]: valeur });
      setDossiers((p) => p.map((d) => (d.id === id ? { ...d, [champ]: valeur } : d)));
    } catch (e) { alert('Erreur : ' + (e.response?.data?.error || e.message)); }
  };

  const enRetard = dossiers.filter((d) => d.protocole_en_retard).length;

  return (
    <>
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher..." value={recherche}
          onChange={(e) => setRecherche(e.target.value)} />
        <input type="date" className="page-select" value={debut} title="Livré à partir du"
          onChange={(e) => setDebut(e.target.value)} />
        <input type="date" className="page-select" value={fin} title="Livré jusqu'au"
          onChange={(e) => setFin(e.target.value)} />
        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          {dossiers.length} véhicule{dossiers.length > 1 ? 's' : ''} livré{dossiers.length > 1 ? 's' : ''}
          {enRetard > 0 && (
            <span className="dc-alerte" style={{ marginLeft: 8 }}>
              ⚠ {enRetard} protocole{enRetard > 1 ? 's' : ''} non signé{enRetard > 1 ? 's' : ''} depuis plus de 48 h
            </span>
          )}
        </span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Date livraison</th>
              <th>Vendeur</th>
              <th>Marque</th>
              <th>Modèle</th>
              <th>Carburant</th>
              <th>N° châssis</th>
              <th>Client</th>
              <th>Immat</th>
              <th style={{ width: 62, textAlign: 'center' }} title="Protocole de livraison signé">Protocole</th>
              <th>Remarques</th>
              <th>Back-office</th>
            </tr>
          </thead>
          <tbody>
            {chargement ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40 }}>Chargement…</td></tr>
            ) : dossiers.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Aucun véhicule livré sur cette période
              </td></tr>
            ) : dossiers.map((d) => (
              <tr key={d.id} className={d.protocole_en_retard ? 'dc-bo-retard' : ''}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(d.date_livraison_reelle || d.date_livraison_prevue)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{d.vendeur_prenom} {d.vendeur_nom}</td>
                <td>{d.vehicule_marque}</td>
                <td>{d.vehicule_modele}</td>
                <td>{d.vehicule_carburant || '—'}</td>
                <td style={{ fontSize: '0.78rem', fontFamily: 'monospace' }} title={d.vehicule_vin || ''}>
                  {d.vehicule_vin ? d.vehicule_vin.slice(-8) : '—'}
                </td>
                <td style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--color-accent)' }}
                  onClick={() => onOpenDossier(d.id)} title="Ouvrir le dossier">
                  {d.client_prenom} {d.client_nom}
                </td>
                <td style={{ fontSize: '0.85rem' }}>{d.vehicule_immatriculation || '—'}</td>
                <td style={{ textAlign: 'center' }}
                  title={d.protocole_en_retard ? 'Livré il y a plus de 48 h et toujours pas signé' : ''}>
                  <input type="checkbox" checked={!!d.livraison_protocole_signe}
                    onChange={(e) => modifier(d.id, 'livraison_protocole_signe', e.target.checked)} />
                  {d.protocole_en_retard && <span className="dc-alerte">⚠</span>}
                </td>
                <td>
                  <input className="dc-livraison-input dc-livraison-obs" placeholder="—"
                    value={d.livraison_observations || ''}
                    onChange={(e) => modifier(d.id, 'livraison_observations', e.target.value)} />
                </td>
                <td>
                  <input className="dc-livraison-input" placeholder="—"
                    value={d.livraison_backoffice || ''}
                    onChange={(e) => modifier(d.id, 'livraison_backoffice', e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LivraisonModal — tous les champs d'une livraison, à la verticale
// ─────────────────────────────────────────────────────────────────────────────
// Le tableau porte quinze colonnes : elles ne tiennent pas à l'écran, et le
// défilement horizontal fait perdre la ligne qu'on suivait. La modale montre
// tout d'un coup, sur une seule ligne de lecture.
//
// ⚠ Les champs du VÉHICULE et du CLIENT sont en lecture seule : ils
// appartiennent au dossier, pas à la livraison. Les rendre modifiables ici
// donnerait deux endroits pour la même donnée. Le bouton du pied mène au
// dossier complet.
function LivraisonModal({ livraison, onClose, onChange, onOpenDossier, onMarquerLivre, enRetard }) {
  if (!livraison) return null;
  const l = livraison;
  const lecture = [
    ['Vendeur', `${l.vendeur_prenom || ''} ${l.vendeur_nom || ''}`.trim()],
    ['Client', `${l.client_prenom || ''} ${l.client_nom || ''}`.trim()],
    ['Marque', l.vehicule_marque],
    ['Modèle', l.vehicule_modele],
    ['Finition', l.vehicule_version],
    ['Couleur', l.vehicule_couleur],
    ['N° châssis', l.vehicule_vin],
    ['Immatriculation', l.vehicule_immatriculation],
    ['Date de livraison', formatDate(l.date_livraison_prevue)],
  ];
  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🚚 Livraison — {l.numero_dossier}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {enRetard && (
            <p className="dc-livraison-alerte">
              ⚠ Livré il y a plus de 48 h et le protocole n'est toujours pas signé.
            </p>
          )}

          <div className="dc-form-section-title">Véhicule &amp; client</div>
          <div className="dc-livraison-lecture">
            {lecture.map(([k, v]) => (
              <div key={k}><span>{k}</span><b>{v || '—'}</b></div>
            ))}
          </div>

          <div className="dc-form-section-title" style={{ marginTop: 16 }}>Livraison</div>
          <div className="dc-form-grid">
            <div className="dc-form-field">
              <label>Livré</label>
              <label className="dc-case">
                <input type="checkbox" checked={l.statut === 'livre'}
                  onChange={(e) => onMarquerLivre(l.id, e.target.checked)} />
                <span>Le véhicule a été livré</span>
              </label>
            </div>
            <div className="dc-form-field">
              <label>Heure</label>
              <input type="time" value={l.livraison_heure || ''}
                onChange={(e) => onChange(l.id, 'livraison_heure', e.target.value)} />
            </div>
            <div className="dc-form-field">
              <label>Carburant</label>
              <input value={l.vehicule_carburant || ''} placeholder="Essence, Diesel, Hybride…"
                onChange={(e) => onChange(l.id, 'vehicule_carburant', e.target.value)} />
            </div>
            <div className="dc-form-field">
              <label>Prêt à immatriculer</label>
              <label className="dc-case">
                <input type="checkbox" checked={!!l.livraison_pret_immat}
                  onChange={(e) => onChange(l.id, 'livraison_pret_immat', e.target.checked)} />
                <span>Oui</span>
              </label>
            </div>
            <div className="dc-form-field">
              <label>Protocole de livraison signé</label>
              <label className="dc-case">
                <input type="checkbox" checked={!!l.livraison_protocole_signe}
                  onChange={(e) => onChange(l.id, 'livraison_protocole_signe', e.target.checked)} />
                <span>Signé</span>
              </label>
            </div>
            <div className="dc-form-field">
              <label>Bonus / Prime</label>
              <input value={l.livraison_bonus_prime || ''}
                onChange={(e) => onChange(l.id, 'livraison_bonus_prime', e.target.value)} />
            </div>
            <div className="dc-form-field full">
              <label>Remarques</label>
              <textarea rows={2} value={l.livraison_observations || ''}
                onChange={(e) => onChange(l.id, 'livraison_observations', e.target.value)} />
            </div>
            <div className="dc-form-field full">
              <label>Back-office</label>
              <textarea rows={2} value={l.livraison_backoffice || ''}
                onChange={(e) => onChange(l.id, 'livraison_backoffice', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={() => { onClose(); onOpenDossier(l.id); }}>
            Ouvrir le dossier complet
          </button>
          <button className="btn-primary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function LivraisonsTab({ onOpenDossier }) {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  // La ligne ouverte en détail. On garde son IDENTIFIANT et non l'objet : la
  // modale doit refléter les modifications faites depuis elle, or la liste est
  // la seule source à jour.
  const [ouvertId, setOuvertId] = useState(null);
  const [livraisons, setLivraisons] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadPlanning = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossiersCommerciaux.getPlanning(date);
      setLivraisons(res.data.livraisons);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Erreur chargement planning:', e);
    }
    setLoading(false);
  }, [date]);

  useEffect(() => { loadPlanning(); }, [loadPlanning]);

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().split('T')[0]);
  };

  // Cocher « Livré » fait passer le dossier au statut `livre` : c'est CE statut
  // qui le fait apparaître dans Back-office. Il ne quitte pas cette liste pour
  // autant — il y reste grisé, à sa date.
  const marquerLivre = async (dossierId, livre) => {
    try {
      await dossiersCommerciaux.update(dossierId, {
        statut: livre ? 'livre' : 'en_cours',
        // La date réelle n'a de sens que si la livraison a eu lieu.
        date_livraison_reelle: livre ? new Date().toISOString().split('T')[0] : null,
      });
      setLivraisons(prev => prev.map(l => (l.id === dossierId
        ? { ...l, statut: livre ? 'livre' : 'en_cours' } : l)));
    } catch (e) {
      alert('Erreur : ' + (e.response?.data?.error || e.message));
    }
  };

  // ⚠ L'alerte ne porte que sur un véhicule DÉJÀ LIVRÉ : réclamer un protocole
  // signé avant la livraison n'aurait aucun sens.
  const protocoleEnRetard = (l) => {
    if (l.livraison_protocole_signe || l.statut !== 'livre') return false;
    const ref = l.date_livraison_reelle || l.date_livraison_prevue;
    if (!ref) return false;
    return (Date.now() - new Date(ref).getTime()) > 48 * 3600 * 1000;
  };

  const handleFieldChange = async (dossierId, field, value) => {
    try {
      await dossiersCommerciaux.updateLivraison(dossierId, { [field]: value });
      setLivraisons(prev => prev.map(l => l.id === dossierId ? { ...l, [field]: value } : l));
    } catch (e) {
      console.error('Erreur MAJ livraison:', e);
    }
  };

  const dateObj = new Date(date + 'T00:00:00');
  const dateLabel = dateObj.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const isToday = date === new Date().toISOString().split('T')[0];

  return (
    <>
      {/* Date navigation */}
      <div className="dc-livraisons-nav">
        <button className="btn-secondary" onClick={() => changeDate(-1)}>←</button>
        <button className={`btn-secondary ${isToday ? 'active-filter' : ''}`} onClick={() => setDate(new Date().toISOString().split('T')[0])}>Aujourd'hui</button>
        <input type="date" className="page-select" value={date} onChange={e => setDate(e.target.value)} />
        <button className="btn-secondary" onClick={() => changeDate(1)}>→</button>
        <span className="dc-livraisons-date-label">{dateLabel}</span>
        <span className="dc-livraisons-count">{total} livraison{total > 1 ? 's' : ''}</span>
      </div>

      {/* Tableau */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 46, textAlign: 'center' }} title="Véhicule livré">Livré</th>
              <th style={{ width: 70 }}>Heure</th>
              <th>Vendeur</th>
              <th>Marque</th>
              <th>Modèle</th>
              <th>Finition</th>
              <th>Couleur</th>
              <th>Carburant</th>
              <th>N° châssis</th>
              <th>Client</th>
              <th>Immat</th>
              <th style={{ width: 50, textAlign: 'center' }} title="Prêt à immatriculer">Prêt immat.</th>
              <th style={{ width: 62, textAlign: 'center' }} title="Protocole de livraison signé">Protocole</th>
              <th>Remarques</th>
              <th>Back-office</th>
              <th style={{ width: 80 }}>Docs</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={16} style={{ textAlign: 'center', padding: 40 }}>Chargement...</td></tr>
            ) : livraisons.length === 0 ? (
              <tr><td colSpan={16} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Aucune livraison prévue ce jour</td></tr>
            ) : (
              livraisons.map(l => (
                // ⚠ Un dossier LIVRÉ reste ici, grisé, à sa date : la journée
                // doit pouvoir se relire. Il apparaît EN PLUS dans Back-office —
                // les deux vues lisent la même ligne, rien n'est recopié.
                // ⚠ `closest('input, textarea, select, button, a')` : sans ce
                // garde-fou, cocher une case ouvrirait aussi la modale — le
                // clic remonte. Cliquer AILLEURS dans la ligne ouvre le détail.
                <tr key={l.id}
                  className={`tbl-clic${l.statut === 'livre' ? ' dc-livre' : ''}`}
                  title="Voir tous les champs"
                  onClick={(e) => { if (!e.target.closest('input, textarea, select, button, a')) setOuvertId(l.id); }}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={l.statut === 'livre'}
                      title="Marquer le véhicule comme livré"
                      onChange={e => marquerLivre(l.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      className="dc-livraison-input"
                      value={l.livraison_heure || ''}
                      onChange={e => handleFieldChange(l.id, 'livraison_heure', e.target.value)}
                    />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{l.vendeur_prenom} {l.vendeur_nom}</td>
                  <td>{l.vehicule_marque}</td>
                  <td>{l.vehicule_modele}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{l.vehicule_version}</td>
                  <td>{l.vehicule_couleur}</td>
                  <td>
                    <input
                      className="dc-livraison-input"
                      style={{ width: 84 }}
                      value={l.vehicule_carburant || ''}
                      placeholder="—"
                      onChange={e => handleFieldChange(l.id, 'vehicule_carburant', e.target.value)}
                    />
                  </td>
                  <td style={{ fontSize: '0.78rem', fontFamily: 'monospace' }} title={l.vehicule_vin || ''}>
                    {l.vehicule_vin ? l.vehicule_vin.slice(-8) : '—'}
                  </td>
                  {/* ⚠ Plus de clic propre à cette cellule : depuis que la LIGNE
                      ouvre le détail, deux gestes se seraient superposés au
                      même endroit — le dossier ET la modale. Le dossier complet
                      s'atteint par le bouton du pied de la modale. */}
                  <td style={{ fontWeight: 600 }}>
                    {l.client_prenom} {l.client_nom}
                  </td>
                  <td style={{ fontSize: '0.85rem' }}>{l.vehicule_immatriculation || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!l.livraison_pret_immat}
                      onChange={e => handleFieldChange(l.id, 'livraison_pret_immat', e.target.checked)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }} title={protocoleEnRetard(l)
                    ? "Livré il y a plus de 48 h et toujours pas signé" : ''}>
                    <input
                      type="checkbox"
                      checked={!!l.livraison_protocole_signe}
                      onChange={e => handleFieldChange(l.id, 'livraison_protocole_signe', e.target.checked)}
                    />
                    {protocoleEnRetard(l) && <span className="dc-alerte">⚠</span>}
                  </td>
                  <td>
                    <input
                      className="dc-livraison-input dc-livraison-obs"
                      value={l.livraison_observations || ''}
                      onChange={e => handleFieldChange(l.id, 'livraison_observations', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    {/* Texte LIBRE tant que l'usage n'est pas fixé : le figer en
                        liste ou en case fermerait des options sans raison. */}
                    <input
                      className="dc-livraison-input"
                      value={l.livraison_backoffice || ''}
                      onChange={e => handleFieldChange(l.id, 'livraison_backoffice', e.target.value)}
                      placeholder="—"
                    />
                  </td>
                  <td>
                    <div className="dc-progress">
                      <div className="dc-progress-bar">
                        <div className="dc-progress-recu" style={{ width: l.docs_total > 0 ? `${((l.docs_fournis || 0) / l.docs_total) * 100}%` : '0%' }} />
                        <div className="dc-progress-fill" style={{ width: l.docs_total > 0 ? `${(l.docs_valides / l.docs_total) * 100}%` : '0%' }} />
                      </div>
                      <span className="dc-progress-text">{l.docs_valides || 0}/{l.docs_total || 0}</span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <LivraisonModal
        livraison={livraisons.find((x) => x.id === ouvertId) || null}
        enRetard={protocoleEnRetard(livraisons.find((x) => x.id === ouvertId) || {})}
        onClose={() => setOuvertId(null)}
        onChange={handleFieldChange}
        onMarquerLivre={marquerLivre}
        onOpenDossier={onOpenDossier}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DocChecklist — Sidebar persistante résumant l'état des documents
// ─────────────────────────────────────────────────────────────────────────────
function DocChecklist({ slots, onNavigate, dropEnabled, onDropPages }) {
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  if (!slots || !slots.length) {
    return (
      <aside className="dc-checklist">
        <div className="dc-checklist-header">
          <span className="dc-checklist-title">📎 Documents</span>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', padding: '8px 0' }}>Aucun document requis</p>
      </aside>
    );
  }

  // ─── Regroupement par RUBRIQUE ────────────────────────────────────────────
  // Une pièce sans rubrique retombe sur « Dossier général » : elle doit rester
  // visible, jamais disparaître faute de classement.
  //
  // ⚠ L'ordre des rubriques suit le `ordre` le plus petit de leurs pièces, et
  // non l'ordre alphabétique : le classement métier déjà saisi dans la
  // configuration commande l'affichage, sans second réglage à tenir à jour.
  // (calcul direct : on est après un retour anticipé, pas de hook ici — et la
  // liste compte une vingtaine de pièces, pas de quoi mémoïser.)
  const rubriques = (() => {
    const m = new Map();
    slots.forEach((x) => {
      const r = (x.rubrique || '').trim() || 'Dossier général';
      if (!m.has(r)) m.set(r, { nom: r, rang: x.ordre ?? 0, pieces: [] });
      const g = m.get(r);
      g.pieces.push(x);
      g.rang = Math.min(g.rang, x.ordre ?? 0);
    });
    return [...m.values()].sort((a, b) => a.rang - b.rang || a.nom.localeCompare(b.nom, 'fr'));
  })();

  const total = slots.length;
  const validated = slots.filter(s => s.documents?.some(d => d.statut === 'valide')).length;
  const uploaded = slots.filter(s => s.documents?.some(d => d.statut === 'uploade')).length;
  const refused = slots.filter(s => s.documents?.some(d => d.statut === 'refuse')).length;
  const missing = total - validated - uploaded - refused;
  const pct = total > 0 ? Math.round((validated / total) * 100) : 0;
  // La collecte avance dès qu'une pièce arrive ; la validation attend un humain.
  const pctRecu = total > 0 ? Math.round(((validated + uploaded) / total) * 100) : 0;

  return (
    <aside className="dc-checklist">
      <div className="dc-checklist-header">
        <span className="dc-checklist-title">📎 Documents</span>
        {/* ⚠ Une seule valeur, et qui ne passe JAMAIS à la ligne : le double
            pourcentage débordait sur deux lignes et poussait le titre vers le
            bas. La distinction reçu / validé se lit déjà dans les deux segments
            de la barre, juste en dessous, et l'infobulle la chiffre. */}
        <span className="dc-checklist-pct" title={`${pctRecu} % des pièces reçues · ${pct} % validées`}>
          {pct} %
        </span>
      </div>

      <div className="dc-progress" style={{ marginBottom: 12 }}>
        <div className="dc-progress-bar" style={{ height: 8 }}>
          <div className="dc-progress-recu" style={{ width: `${pctRecu}%` }} />
          <div className="dc-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="dc-progress-text">{validated}/{total}</span>
      </div>

      <div className="dc-checklist-counts">
        {validated > 0 && <span className="dc-checklist-count valide">{validated} validé{validated > 1 ? 's' : ''}</span>}
        {uploaded > 0 && <span className="dc-checklist-count uploade">{uploaded} en attente</span>}
        {refused > 0 && <span className="dc-checklist-count refuse">{refused} refusé{refused > 1 ? 's' : ''}</span>}
        {missing > 0 && <span className="dc-checklist-count manquant">{missing} manquant{missing > 1 ? 's' : ''}</span>}
      </div>

      <div className="dc-checklist-list">
        {rubriques.map(groupe => (
          <div key={groupe.nom} className="dc-checklist-groupe">
            {/* Un seul groupe ne mérite pas d'en-tête : ce serait du bruit. */}
            {rubriques.length > 1 && (
              <div className="dc-checklist-rubrique">
                {groupe.nom}
                <span>{groupe.pieces.filter(s => s.documents?.length).length}/{groupe.pieces.length}</span>
              </div>
            )}
        {groupe.pieces.map(slot => {
          const doc = slot.documents && slot.documents[0];
          const statut = doc ? doc.statut : 'manquant';
          const canDrop = dropEnabled && !doc;
          const isDragOver = dragOverSlotId === slot.id;
          return (
            <div
              key={slot.id}
              className={`dc-checklist-item ${statut} ${canDrop ? 'dc-checklist-drop-target' : ''} ${isDragOver ? 'dc-checklist-drag-over' : ''}`}
              onClick={onNavigate}
              title={`${slot.nom} — ${DOC_STATUS_LABELS[statut]}`}
              onDragOver={canDrop ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverSlotId(slot.id); } : undefined}
              onDragEnter={canDrop ? (e) => { e.preventDefault(); setDragOverSlotId(slot.id); } : undefined}
              onDragLeave={canDrop ? () => setDragOverSlotId(null) : undefined}
              onDrop={canDrop ? (e) => {
                e.preventDefault();
                setDragOverSlotId(null);
                try {
                  const data = e.dataTransfer.getData('application/x-dc-pages');
                  if (data && onDropPages) {
                    const pageNums = JSON.parse(data);
                    onDropPages(slot.id, pageNums);
                  }
                } catch (err) { console.error('Drop error:', err); }
              } : undefined}
            >
              <span className={`dc-checklist-dot ${statut}`} />
              <span className="dc-checklist-label">
                {slot.nom}
                {slot.obligatoire && <span style={{ color: 'var(--color-error)' }}> *</span>}
              </span>
              {doc?.ia_statut === 'ok' && <span className="dc-checklist-ia ok" title="IA OK">✓</span>}
              {doc?.ia_statut === 'alerte' && <span className="dc-checklist-ia alerte" title="Alerte IA">⚠</span>}
              {doc?.ia_statut === 'erreur' && <span className="dc-checklist-ia erreur" title="Erreur IA">✕</span>}
              {doc && !doc.ia_statut && doc.s3_key && <span className="dc-checklist-ia pending" title="Analyse en cours">⏳</span>}
              <span className={`dc-checklist-badge ${slot.categorie}`}
                title={slot.categorie === 'vendeur' ? 'À fournir par le vendeur' : 'À fournir par la secrétaire'}>
                {slot.categorie === 'vendeur' ? 'V' : 'S'}
              </span>
            </div>
          );
        })}
          </div>
        ))}
      </div>
      {/* Une pastille d'une seule lettre n'explique rien : sans légende, il faut
          deviner ou demander. Elle se lit ici, sous la liste qu'elle qualifie. */}
      <div className="dc-checklist-legende">
        <span><span className="dc-checklist-badge vendeur">V</span> vendeur</span>
        <span><span className="dc-checklist-badge secretaire">S</span> secrétaire</span>
        <span><span style={{ color: 'var(--color-error)' }}>*</span> pièce obligatoire</span>
      </div>
    </aside>
  );
}

export default GestionCommerciale;
